import {
	DefaultExcludedAdjustmentKeys,
	getDefaultAdjustments,
	interpolateFullAdjustments,
} from "@/core/adjustments"
import {
	Adjustments,
	LookTable,
	LookTableType,
	LookTableVersion,
	LookTableEncoding,
	LookTableRGBPrimaries,
	LookTableRGBGamma,
} from "@/core/types"
import { MainLUTResolver } from "@/io/assets/lut/lut-resolver"
import { AI } from "@/lib/ai/ai"
import { PhotoRecord, HistoryRecordAction } from "@/lib/catalog/types"
import { getPhotoMetadata, photosAtom } from "../photo/photos-state"
import { storeAdjustmentsHistoryAndUpdatePhoto } from "../processing/processing-storage"
import { projectAtom } from "../project/projects-state"
import { store } from "../store"
import { getFileForPhoto } from "../files-state"
import { ColorMatchImageItem, ColorMatchImageService } from "./color-match-service"
import { getOrExtractImageInfo, updateImageInfo } from "../processing/image-info"
import { fetchCurrentAdjustmentsFromHistory } from "../store-history"
import { isColorMatchDebugEnabled } from "@/utils/measurement/feature-flag"
import { WhiteBalance } from "@/core/photo"
import {
	getInputColorMatchFeatureWithFaceData,
	saveInputColorMatchFeatureToImageInfo,
} from "../processing/processing-utils"
import { transfer } from "comlink"
import { getReferenceCacheId } from "@/lib/ai/color-match/utils"
import { colorMatchCache } from "./color-match-cache"
import { InputColorMatchFeaturesWithFaceData } from "@/lib/ai/color-match/utils"
import { resizeImageDataToBitmap } from "@/core/utils/resize-image"
import { adjustmentsContainsNaN, AI_WORKER_REF_CACHE_MISS_ERROR } from "@/lib/ai/utils"
import { getAutoLightParams } from "@/lib/ai/calibration/ai-calibration-brightness"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { replaceDialog } from "../dialogs-state"
import { getAutoWBParamsForColorMatch } from "@/lib/ai/calibration/ai-calibration-wb"
import { AbortError } from "@/utils/error"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

export async function getColorMatchAdjustments(inputs: {
	photo: PhotoRecord
	referenceImage: ColorMatchImageItem
	useParameters: boolean
	strength: number
	skinToneOptimized: boolean
	asShotWhiteBalance: WhiteBalance
	shouldCompute?: boolean
	forceRecompute?: boolean
}) {
	let adjustments = getSavedColorMatchAdjustments(inputs)

	if (inputs.forceRecompute || (!adjustments && inputs.shouldCompute)) {
		adjustments = await computeColorMatchAdjustments(inputs)
	}

	if (!adjustments) {
		return
	}

	adjustments = await getColorMatchAdjustmentsForStrength({
		...inputs,
		adjustments,
	})

	return adjustments
}

export function getSavedColorMatchAdjustments(inputs: {
	photo?: PhotoRecord
	referenceImage?: ColorMatchImageItem
	useParameters: boolean
	skinToneOptimized: boolean
}) {
	const { photo, referenceImage, useParameters, skinToneOptimized } = inputs

	let adjustments: Adjustments | undefined = undefined

	if (!photo || !referenceImage) {
		return adjustments
	}

	const colorMatchId = getColorMatchId({ inputId: photo.id, referenceId: referenceImage.id })
	if (useParameters) {
		if (skinToneOptimized) {
			adjustments = colorMatchCache.getSkinToneOptimizedAdjustments(colorMatchId)
		} else {
			adjustments = colorMatchCache.getAdjustments(colorMatchId)
		}
	} else {
		const lut = colorMatchCache.getLut(colorMatchId)
		if (lut) {
			adjustments = colorMatchCache.getLutAdjustments(colorMatchId)
		}
	}
	return adjustments
}

async function getColorMatchAdjustmentsForStrength(inputs: {
	photo: PhotoRecord
	adjustments: Adjustments
	useParameters: boolean
	strength: number
	asShotWhiteBalance: WhiteBalance
}) {
	const { photo, adjustments, useParameters, strength, asShotWhiteBalance } = inputs
	let finalAdjustments = adjustments
	if (adjustments.colorMatchStrength !== strength) {
		if (useParameters) {
			//interpolate
			finalAdjustments = interpolateFullAdjustments(
				{
					...getDefaultAdjustments(photo),
					temperature: asShotWhiteBalance.asShotTemperature,
					tint: asShotWhiteBalance.asShotTint,
				},
				adjustments,
				strength,
			)
		} else {
			finalAdjustments.userLutStrength = strength
		}
	}

	finalAdjustments.colorMatchStrength = strength

	// Preserve non-color adjustments like crop, straighten, etc.
	const currentAdjustments = await fetchCurrentAdjustmentsFromHistory(photo)
	DefaultExcludedAdjustmentKeys.forEach((key) => {
		delete finalAdjustments[key]
	})
	finalAdjustments = { ...currentAdjustments, ...finalAdjustments }
	return finalAdjustments
}

export async function applyColorMatchAdjustments(inputs: {
	photo: PhotoRecord
	referenceImage: ColorMatchImageItem
	useParameters: boolean
	strength: number
	skinToneOptimized: boolean
	asShotWhiteBalance: WhiteBalance
	generatePreview: boolean
	forceRecompute?: boolean
}) {
	const { referenceImage, useParameters, strength, skinToneOptimized } = inputs
	//Get the latest photo data to ensure we have the most up-to-date photo object
	const latestPhoto = store.get(photosAtom)[inputs.photo.id]
	if (!latestPhoto || latestPhoto.isHidden) {
		throw new AbortError("Photo is hidden")
	}

	const file = getFileForPhoto(latestPhoto.id)
	if (!file) {
		return
	}

	try {
		await AI.lockEditor()

		const adjustments = await getColorMatchAdjustments({
			...inputs,
			photo: latestPhoto,
			shouldCompute: true,
			forceRecompute: inputs.forceRecompute,
		})
		if (!adjustments || adjustmentsContainsNaN(adjustments)) {
			replaceDialog(ConfirmDialog, {
				trackingViewName: "color_match_error_dialog",
				title: i18n.t("store:colorMatchState.colorMatchErrorTitle"),
				message: i18n.t("store:colorMatchState.colorMatchErrorMessage"),
				primary: {
					title: i18n.t("common:action.retry"),
					handler: () => {
						void applyColorMatchAdjustments({ ...inputs, forceRecompute: true })
					},
				},
				secondary: {
					title: i18n.t("common:action.cancel"),
					handler: () => {
						void AI.unlockEditor()
					},
				},
			})
			return
		}

		//Generate preview
		let preview: Blob | null = null
		if (inputs.generatePreview) {
			await AI.loadImage(file, { metadata: getPhotoMetadata(latestPhoto.id) })
			preview = await AI.generatePreview({ adjustments })
		}

		const updatedPhoto = store.get(photosAtom)[latestPhoto.id]
		if (!updatedPhoto || updatedPhoto.isHidden) {
			throw new AbortError("Photo is hidden")
		}

		//Save results
		const message = getColorMatchHistoryMessage(
			useParameters,
			skinToneOptimized,
			referenceImage.name,
		)

		await storeAdjustmentsHistoryAndUpdatePhoto(
			updatedPhoto,
			{ adjustments, preview, imageInfo: null, originalPreview: null },
			{
				message,
				action: HistoryRecordAction.ApplyColorMatch,
				changedValue: `${Math.round(strength * 100)}`,
			},
		)
	} finally {
		void AI.unlockEditor()
	}
}

function getColorMatchId(options: { inputId: string; referenceId: string }) {
	return "color-match|reference-" + options.referenceId + "|input-" + options.inputId
}

async function computeColorMatchAdjustments(inputs: {
	photo: PhotoRecord
	referenceImage: ColorMatchImageItem
	useParameters: boolean
	skinToneOptimized: boolean
}) {
	const { photo, referenceImage, useParameters, skinToneOptimized } = inputs

	const referenceImageId = referenceImage.id
	const project = store.get(projectAtom)
	const metadata = getPhotoMetadata(photo.id)

	if (!project || !metadata) {
		return
	}

	// INPUT IMAGE
	const file = getFileForPhoto(photo.id)
	if (!file) {
		throw new Error("File not found")
	}
	await AI.loadImage(file, { metadata: getPhotoMetadata(photo.id) })

	// INPUT FEATURES
	const imageInfo = await getOrExtractImageInfo(photo)
	const existingInputFeatures = colorMatchCache.getInputFeatures(photo.id)
	const inputFeatures = getInputColorMatchFeatureWithFaceData(imageInfo) ?? existingInputFeatures
	const baseAdjustments: Partial<Adjustments> =
		imageInfo.features.autoLightParams ??
		getAutoLightParams(
			imageInfo.features.light,
			imageInfo.features.colorMatch?.avgFaceHsvl ?? null,
		)
	const baseWB =
		imageInfo.features.autoWbParams ??
		getAutoWBParamsForColorMatch({
			illuminant: imageInfo.features.wb,
			asShotTemperature: imageInfo.features.meta.init_temp ?? 5000,
			asShotTint: imageInfo.features.meta.init_tint ?? 0,
		})
	baseAdjustments.temperature = baseWB.temperature
	baseAdjustments.tint = baseWB.tint
	// Note: the baseAdjustments are just for better starting input image for the neural network, we will not use them in the final adjustments with computeFullParams = true (full color match path).
	// If you want to simulate half color match path, you can use the baseAdjustments = fetchCurrentAdjustmentsFromHistory(photo) and computeFullParams = false.

	// REFERENCE IMAGE AND FEATURES
	const { referenceFeatures, referenceId } =
		await getRefFeaturesAndSetCacheIfNeeded(referenceImage)

	// AI CALL
	let result: {
		adjustments: Adjustments | null
		adjustmentsSkinToneOptimized: Adjustments
		mergedLut3d: Float32Array
		computedInputFeatures: InputColorMatchFeaturesWithFaceData | null
	}
	const params = {
		photo,
		inputFeatures,
		outputLut: isColorMatchDebugEnabled() || !useParameters,
		outputParamsWithoutFaceOptimization: isColorMatchDebugEnabled(),
		referenceId,
		referenceFeatures,
		baseAdjustments,
		computeFullParams: true,
		computeWb: true,
	}

	// Just a safety guard: retry if cache miss (supposedly cache sync issue)
	try {
		result = await AI.getNeuralColorMatchParams(params)
	} catch (error) {
		if (error instanceof Error && error.message.includes(AI_WORKER_REF_CACHE_MISS_ERROR)) {
			console.warn(`Cache sync issue detected for reference ${referenceId}, retrying...`)

			colorMatchCache.removeAiWorkerReferenceBitmap(referenceId)
			await getRefFeaturesAndSetCacheIfNeeded(referenceImage)
			result = await AI.getNeuralColorMatchParams(params)
		} else {
			throw error
		}
	}

	const { adjustments, adjustmentsSkinToneOptimized, mergedLut3d, computedInputFeatures } = result

	// Likely corrupted input, don't even cache the results and features and exit now
	if (adjustmentsContainsNaN(adjustmentsSkinToneOptimized)) {
		console.error("Adjustments contain NaN", { adjustmentsSkinToneOptimized, params })
		return
	}

	if (!inputFeatures && computedInputFeatures) {
		// save computed input features to imageInfo or cache (imageInfo may be not available in manual mode)
		if (imageInfo) {
			saveInputColorMatchFeatureToImageInfo(imageInfo, computedInputFeatures)
			updateImageInfo(imageInfo)
		} else {
			colorMatchCache.setInputFeatures(photo.id, computedInputFeatures)
		}
	}

	const colorMatchId = getColorMatchId({ inputId: photo.id, referenceId: referenceImageId })

	adjustmentsSkinToneOptimized.colorMatchImageId = referenceImageId
	colorMatchCache.setSkinToneOptimizedAdjustments(colorMatchId, adjustmentsSkinToneOptimized)

	if (adjustments) {
		adjustments.colorMatchImageId = referenceImageId
		colorMatchCache.setAdjustments(colorMatchId, adjustments)
	}

	const lutAdjustments = { ...getDefaultAdjustments(photo), ...(baseAdjustments ?? {}) }
	lutAdjustments.userLutId = colorMatchId
	lutAdjustments.colorMatchImageId = referenceImageId
	if (isColorMatchDebugEnabled() || !useParameters) {
		const lut: LookTable = {
			id: colorMatchId,
			data: mergedLut3d,
			type: LookTableType.rgb,
			version: LookTableVersion.version1,
			encoding: LookTableEncoding.sRGB,
			hueDivisions: 25,
			satDivisions: 25,
			valDivisions: 25,
			rgbPrimaries: LookTableRGBPrimaries.sRGB,
			rgbGamma: LookTableRGBGamma.sRGB,
			minAmount: 0,
			maxAmount: 1,
		}
		colorMatchCache.setLut(colorMatchId, lut)
		MainLUTResolver.add(lut)
		colorMatchCache.setLutAdjustments(colorMatchId, lutAdjustments)
	}

	return useParameters
		? skinToneOptimized
			? adjustmentsSkinToneOptimized
			: (adjustments ?? adjustmentsSkinToneOptimized)
		: lutAdjustments
}

async function getRefFeaturesAndSetCacheIfNeeded(referenceImage: ColorMatchImageItem) {
	const referenceImageId = referenceImage.id
	const referenceId = getReferenceCacheId({ referenceImageId })
	const referenceCached = colorMatchCache.hasReferenceBitmapInAIWorker(referenceId)
	let referenceFeatures = colorMatchCache.getReferenceFeatures(referenceId)

	// if not cached in AI worker, create image bitmap and extract features / set cache
	if (!referenceCached || !referenceFeatures) {
		let referenceImageData = referenceImage.data
		if (!referenceImageData || !(referenceImageData instanceof Blob)) {
			referenceImageData = await ColorMatchImageService.downloadImageContent(referenceImage)
			if (!referenceImageData) {
				throw new Error("Reference image data not available.")
			}
		}
		const referenceImageBitmap = await createImageBitmap(referenceImageData)

		if (!referenceFeatures) {
			const { referenceColorMatchFeatures } =
				await AI.getReferenceColorMatchFeaturesAndSetCache(
					transfer(
						{
							imageBitmap: referenceImageBitmap,
							imageID: referenceImageId,
							setCache: true,
						},
						[referenceImageBitmap],
					),
				)

			referenceFeatures = referenceColorMatchFeatures
			colorMatchCache.setReferenceFeatures(referenceId, referenceFeatures)
		} else {
			const resizedBitmap = await resizeImageDataToBitmap(
				referenceImageData,
				256,
				256,
				"high",
			)
			await AI.setReferenceBitmapCache(referenceId, transfer(resizedBitmap, [resizedBitmap]))
			referenceImageBitmap.close()
		}

		colorMatchCache.setAiWorkerReferenceBitmap(referenceId)
	}

	return { referenceFeatures, referenceId }
}

export const getColorMatchHistoryMessage = (
	useParameters: boolean,
	skinToneOptimized: boolean,
	imageName: string,
) => {
	let message = "Apply Color Match: "
	if (isColorMatchDebugEnabled()) {
		message = useParameters
			? skinToneOptimized
				? "Apply Color Match (Skin Tone Optimized): "
				: "Apply Color Match: "
			: "Apply Color Match (LUT): "
	}
	message += imageName

	return message
}
